import * as THREE from "three";
import type * as maptilersdk from "@maptiler/sdk";

import {
  createDebugMaterial,
  createGlobeVisualizationHelper,
  USE_DEBUG_OBJECTS_3D,
  USE_DEBUG_PARTICLES_TEXTURE,
} from "../debug";
import type { Tile, VerticesRow } from "../types";
import { areArraysEqual, getMapElement, slerpVectors } from "../utils";

import { projectTileCoordinatesToSphere } from "./maplibre";
import { EXTENT, GLOBE_MAX_ZOOM } from "./constants";
import { getTilesGrid, getAllTilesForZoom, getTileForCoordinates, getGridSizeForPitchAndMapDimensions } from "./utils";

type InitProps = {
  map: maptilersdk.Map;
  minZoom: number;
  maxZoom: number;
};

/**
 * How many vertices will be interpolated on the poles.
 */
const INTERPOLATION_RANGE = 4;

class GlobeTilesService {
  public readonly object3D = new THREE.Group();
  public readonly helpersObject3D = new THREE.Group();

  private map: maptilersdk.Map | undefined = undefined;
  public currentTiles: Array<Tile> = [];

  /**
   * Tiles mesh for data which can be tiled.
   */
  public readonly currentTilesMeshes = new Map<string, THREE.Mesh>();

  /**
   * Mesh for visible tiles area - e.g. to display particles
   */
  public visibleTilesAreaMesh = new THREE.Mesh();

  /**
   * Rows of vertices, rowId is a abstract identifier (related to tile but not exactly the same).
   * This is used to create full area mesh.
   */
  private areaGeometryVerticesRows: Map<string, VerticesRow> = new Map();

  /**
   * Min zoom level for tiles
   */
  private minZoom = 0;

  /**
   * Max zoom level for tiles
   * 0-24 - https://maplibre.org/maplibre-gl-js/docs/API/type-aliases/MapOptions/#maxzoom
   */
  private maxZoom = 24;

  /**
   * Flag to check if geometry needs to be recreated (after visible tiles change).
   */
  private isGeometryNeedRefresh = false;

  constructor() {
    const globeVisualizationHelper = createGlobeVisualizationHelper();
    this.helpersObject3D.add(globeVisualizationHelper);

    this.object3D.add(this.visibleTilesAreaMesh);
    this.visibleTilesAreaMesh.material = createDebugMaterial({ color: 0xffffff, wireframe: false });
    this.visibleTilesAreaMesh.renderOrder = 2;

    if (USE_DEBUG_PARTICLES_TEXTURE === true) {
      this.visibleTilesAreaMesh.visible = true;
    } else {
      this.visibleTilesAreaMesh.visible = false;
    }
  }

  /**
   * Initialize the service.
   * Using it to create the object at the beginning and initialize it map is ready.
   * The idea is to simplify calling the `update` method.
   */
  public init({ map, minZoom, maxZoom }: InitProps) {
    this.map = map;
    this.minZoom = minZoom;
    this.maxZoom = maxZoom;
  }

  /**
   * Update list of tiles to display.
   */
  public update() {
    if (this.map === undefined) {
      return;
    }
    let zoom = Math.floor(this.map.getZoom());
    zoom = Math.min(Math.max(zoom, this.minZoom), this.maxZoom);

    const center = this.map.getCenter();
    const pitch = this.map.getPitch();

    const { gridSize } = getGridSizeForPitchAndMapDimensions(pitch, this.map);
    const centerTile = getTileForCoordinates(center, zoom);

    const tiles = zoom <= GLOBE_MAX_ZOOM - 1 ? getAllTilesForZoom(zoom) : getTilesGrid(centerTile, gridSize);

    if (
      !areArraysEqual(
        this.currentTiles.map(({ id }) => id),
        tiles.map(({ id }) => id),
      )
    ) {
      this.currentTiles = tiles;
      this.isGeometryNeedRefresh = true;
      this.object3D.remove(this.visibleTilesAreaMesh);
    }
  }

  /**
   * Recreate:
   * - each tile mesh
   * - area mesh
   */
  public recreateGlobeMeshes() {
    if (this.isGeometryNeedRefresh === false) {
      return;
    }

    this.isGeometryNeedRefresh = false;

    this.object3D.clear();
    this.helpersObject3D.clear();
    this.currentTilesMeshes.clear();
    this.areaGeometryVerticesRows.clear();

    this.recreateGlobeTiles();
    this.recreateAreaMesh();
  }

  /**
   * Recreate tiles meshes.
   * Used for tiling friendly data (temperature, pressure, etc.).
   */
  private recreateGlobeTiles() {
    const resolution = 16;

    const lowestTileX = this.currentTiles.reduce((acc, tile) => Math.min(acc, tile.x), Number.POSITIVE_INFINITY);
    const lowestTileY = this.currentTiles.reduce((acc, tile) => Math.min(acc, tile.y), Number.POSITIVE_INFINITY);

    for (const tile of this.currentTiles) {
      const { z, x, y } = tile;
      const lastTileY = 2 ** z - 1;

      const geometry = new THREE.BufferGeometry();
      const positions = [];
      const indices = [];
      const uvs = [];

      /**
       * Slipy tiles directions:
       *
       *    x 0 1 2 3
       * y
       * 0
       * 1
       * 2
       * 3
       */

      const tileColumn = x;
      const tileRow = y;

      for (let i = 0; i <= resolution; i++) {
        const vertexRowIndex = i;
        for (let j = 0; j <= resolution; j++) {
          const vertexColumnIndex = j;

          const uv_u = j / resolution;
          const uv_v = i / resolution;
          const flipped_uv_v = 1 - uv_v;

          const u = uv_u * EXTENT;
          const v = uv_v * EXTENT;

          let vertex = projectTileCoordinatesToSphere(u, v, x, y, z);

          /**
           * This is to join top edges of the tiles to get rid of holes on poles.
           */
          if (tileRow === 0) {
            if (vertexRowIndex <= INTERPOLATION_RANGE) {
              const interpolationFactor = 1 - vertexRowIndex / INTERPOLATION_RANGE;
              vertex = slerpVectors(vertex, [0, 1, 0], interpolationFactor);
            }
          }

          if (tileRow === lastTileY) {
            if (vertexRowIndex >= resolution - INTERPOLATION_RANGE) {
              const interpolationFactor = 1 - (resolution - vertexRowIndex) / INTERPOLATION_RANGE;
              vertex = slerpVectors(vertex, [0, -1, 0], interpolationFactor);
            }
          }
          /* *** */

          positions.push(vertex[0], vertex[1], vertex[2]);
          uvs.push(uv_u, flipped_uv_v);

          const isFirstRow = vertexRowIndex === 0;
          const isFirstColumn = vertexColumnIndex === 0;
          const isLowestRow = tileRow === lowestTileY;
          const isLowestColumn = tileColumn === lowestTileX;

          if (
            (isFirstRow === false && isFirstColumn === false) ||
            (isFirstRow === true && isFirstColumn === false && isLowestRow === true) ||
            (isFirstColumn === true && isFirstRow === false && isLowestColumn === true) ||
            (isLowestRow === true && isLowestColumn === true)
          ) {
            const verticesRowId = `tileY:${tileRow}__vertexRow:${vertexRowIndex}`;

            if (this.areaGeometryVerticesRows.has(verticesRowId) === false) {
              this.areaGeometryVerticesRows.set(verticesRowId, []);
            }

            const row = getMapElement(this.areaGeometryVerticesRows, verticesRowId);
            row.push(vertex);
          }

          if (i !== resolution && j !== resolution) {
            const a = i * (resolution + 1) + j;
            const b = (i + 1) * (resolution + 1) + j;
            const c = (i + 1) * (resolution + 1) + (j + 1);
            const d = i * (resolution + 1) + (j + 1);
            indices.push(a, b, d);
            indices.push(b, c, d);
          }
        }
      }

      // Code bellow is moved to single loop (above) for better performance (this version is here only as reference)
      // for (let i = 0; i < resolution; i++) {
      //   for (let j = 0; j < resolution; j++) {
      //     const a = i * (resolution + 1) + j;
      //     const b = (i + 1) * (resolution + 1) + j;
      //     const c = (i + 1) * (resolution + 1) + (j + 1);
      //     const d = i * (resolution + 1) + (j + 1);
      //     indices.push(a, d, b);
      //     indices.push(b, d, c);
      //   }
      // }

      geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
      geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
      geometry.setIndex(indices);

      const mesh = new THREE.Mesh();
      mesh.geometry = geometry;
      mesh.renderOrder = 1;

      this.object3D.add(mesh);

      if (USE_DEBUG_OBJECTS_3D === true) {
        const wireframeMesh = new THREE.Mesh();
        wireframeMesh.geometry = geometry;
        wireframeMesh.material = createDebugMaterial({ wireframe: true, opacity: 0.1 });
        wireframeMesh.renderOrder = 2;
        this.helpersObject3D.add(wireframeMesh);
      }

      this.currentTilesMeshes.set(tile.id, mesh);
    }
  }

  /**
   * Recreate single mesh for visible tiles area.
   * Used for particles display.
   */
  private recreateAreaMesh() {
    // Sort geometry rows to crate it from top to bottom.
    const sortedRowKeys = Array.from(this.areaGeometryVerticesRows.keys()).sort((a, b) => {
      const [tileY_a, rowIndex_a] = a.split("__").map((x) => Number(x.split(":")[1]));
      const [tileY_b, rowIndex_b] = b.split("__").map((x) => Number(x.split(":")[1]));

      if (tileY_a === tileY_b) {
        return rowIndex_a - rowIndex_b;
      }

      return tileY_a - tileY_b;
    });

    const areaGeometryRows = sortedRowKeys.map((key) => this.areaGeometryVerticesRows.get(key) as VerticesRow);

    const rowsAmount = areaGeometryRows.length;
    const columnsAmount = areaGeometryRows[0].length;

    if (rowsAmount < 2) {
      console.error("Not enough rows to create sphere fragment:", { rowsAmount });
      return;
    }

    if (rowsAmount !== columnsAmount) {
      console.warn("Rows amount is not equal to columns amount:", { rowsAmount, columnsAmount });
      return;
    }

    const areaGeometryPositions: number[] = [];
    const areaGeometryUVs: number[] = [];
    const areaGeometryIndices: number[] = [];

    const vertexKeyToIndexMap = new Map<string, number>();

    for (let i = 0; i < rowsAmount; i++) {
      for (let j = 0; j < columnsAmount; j++) {
        const vertex = areaGeometryRows[i][j];
        let key = `${vertex[0]},${vertex[1]},${vertex[2]}`;

        if (vertexKeyToIndexMap.has(key)) {
          // If there are any "duplicate" vertices, add some random noise to make them unique.
          // This could happen when e.g. on the poles.
          vertex[0] += Math.random() * 0.0001;
          vertex[1] += Math.random() * 0.0001;
          vertex[2] += Math.random() * 0.0001;
          key = `${vertex[0]},${vertex[1]},${vertex[2]}`;
        }

        vertexKeyToIndexMap.set(key, vertexKeyToIndexMap.size);
        areaGeometryPositions.push(vertex[0], vertex[1], vertex[2]);

        const u = j / (columnsAmount - 1);
        const v = 1 - i / (rowsAmount - 1);

        areaGeometryUVs.push(u, v);
      }
    }

    // Triangles
    for (let i = 0; i < rowsAmount - 1; i++) {
      const row = areaGeometryRows[i];
      const nextRow = areaGeometryRows[i + 1];

      for (let j = 0; j < columnsAmount - 1; j++) {
        const topLeftKey = `${row[j][0]},${row[j][1]},${row[j][2]}`;
        const topRightKey = `${row[j + 1][0]},${row[j + 1][1]},${row[j + 1][2]}`;
        const bottomLeftKey = `${nextRow[j][0]},${nextRow[j][1]},${nextRow[j][2]}`;
        const bottomRightKey = `${nextRow[j + 1][0]},${nextRow[j + 1][1]},${nextRow[j + 1][2]}`;

        const topLeftIndex = vertexKeyToIndexMap.get(topLeftKey);
        const topRightIndex = vertexKeyToIndexMap.get(topRightKey);
        const bottomLeftIndex = vertexKeyToIndexMap.get(bottomLeftKey);
        const bottomRightIndex = vertexKeyToIndexMap.get(bottomRightKey);

        if (
          topLeftIndex === undefined ||
          topRightIndex === undefined ||
          bottomLeftIndex === undefined ||
          bottomRightIndex === undefined
        ) {
          console.error("Undefined index:", { topLeftIndex, topRightIndex, bottomLeftIndex, bottomRightIndex });
          continue;
        }

        areaGeometryIndices.push(topLeftIndex, bottomLeftIndex, topRightIndex);
        areaGeometryIndices.push(bottomLeftIndex, bottomRightIndex, topRightIndex);
      }
    }

    const areaGeometry = new THREE.BufferGeometry();
    areaGeometry.setAttribute("position", new THREE.Float32BufferAttribute(areaGeometryPositions, 3));
    areaGeometry.setAttribute("uv", new THREE.Float32BufferAttribute(areaGeometryUVs, 2));
    areaGeometry.setIndex(areaGeometryIndices);

    this.visibleTilesAreaMesh.geometry = areaGeometry;
    this.object3D.add(this.visibleTilesAreaMesh);
  }
}

export { GlobeTilesService };
